import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { CreateExplorerEvent } from '~src/common/telemetryEvents/TelemetryEventCreator';
import { ExplorerEvent } from '~src/common/telemetryEvents/TelemetryEvents';
import { Telemetry, ValidTelemetryEvent } from '~src/commonInfra/services/Telemetry';

import { ContentFilterKeyName, ContentFiltersKeys } from '../../../ts/models/ContentFilters';
import {
  mixedContentFilterApplied,
  setContentFilterNoRequestContent
} from '../../actions/spaces/filters/FilterActions';
import { CompositeSessionInfoSelectors } from '../../selectors/SessionInfoSelectors';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';

export interface FilterUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface FilterUpdaterStateProps {
  readonly isLimitedUserVisibilityEnforced: boolean;
}

export interface FilterUpdaterDispatchProps {
  readonly mixedContentFilterApplied: (filterType: ContentFilterKeyName, value: string) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
}

export type FilterUpdaterProps = FilterUpdaterOwnProps & FilterUpdaterStateProps & FilterUpdaterDispatchProps;

export enum FiltersEvents {
  FiltersUpdated = 'FiltersUpdated'
}
interface FiltersUpdatedEvent extends ValidTelemetryEvent<ExplorerEvent>, ValidTelemetryEvent<FiltersUpdatedEvent>  {
  type: FiltersEvents.FiltersUpdated;
  filtersAdded?: string;
  filtersRemoved?: string;
  filtersUpdated?: string;
}

/**
 * Component that syncs the filters from the URL query params to the Redux store before rendering its children.
 */
export class FilterUpdater extends React.Component<FilterUpdaterProps> {
  constructor(props: FilterUpdaterProps) {
    super(props);

    const curQueryValues = queryString.parse(props.routeLocationSearch);
    this.getFilterKeys().forEach((filterKey: ContentFilterKeyName) => {
      props.setContentFilterNoRequestContent(filterKey, getFilterValue(curQueryValues[filterKey]));
    });
  }

  render() {
    return <>{this.props.children}</>;
  }

  componentDidUpdate(prevProps: FilterUpdaterProps) {
    const curQueryValues = queryString.parse(this.props.routeLocationSearch);
    const prevQueryValues = queryString.parse(prevProps.routeLocationSearch);

    // If resetStore is set, dispatch the setContentFilterNoRequestContent action and nothing else
    // TFSID:983648.  Handle case when updating this component with a new projectId
    if (this.props.resetStore) {
      this.getFilterKeys().forEach((filterKey: ContentFilterKeyName) => {
        this.props.setContentFilterNoRequestContent(filterKey, getFilterValue(curQueryValues[filterKey]));
      });
    } else {
      // If the query string did not change, do not update filters in the redux store
      if (prevProps.routeLocationSearch === this.props.routeLocationSearch) return;

      if (prevProps.routeLocationPath === this.props.routeLocationPath) {
        this.getFilterKeys().forEach((filterKey: ContentFilterKeyName) => {
          if (prevQueryValues[filterKey] !== curQueryValues[filterKey]) {
            this.props.mixedContentFilterApplied(filterKey, getFilterValue(curQueryValues[filterKey]));
          }
        });

        logTelemetryIfModified(this.getFilterKeys(), prevQueryValues, curQueryValues);
      }
    }
  }

  private getFilterKeys() {
    return this.props.isLimitedUserVisibilityEnforced
      ? _.without(_.keys(ContentFiltersKeys), ContentFiltersKeys.owner)
      : _.keys(ContentFiltersKeys);
  }
}

function logTelemetryIfModified(allKeys: string[], pValues: any, cValues: any) {
  const { added, removed, updated } = allKeys.reduce((p, c) => {
    if (!_.has(pValues, c) && _.has(cValues, c)) {
      p.added.push(c);
    } else if (_.has(pValues, c) && !_.has(cValues, c)) {
      p.removed.push(c);
    } else if (_.has(pValues, c) && _.has(cValues, c)) {
      if (pValues[c] !== cValues[c]) {
        p.updated.push(c);
      }
    }
    return p;
  }, { added: [], removed: [], updated: [] });

  if (removed.length || added.length || updated.length) {
    Telemetry.sendEvent<FiltersUpdatedEvent>(CreateExplorerEvent({
      type: FiltersEvents.FiltersUpdated,
      ...(added.length) && { filtersAdded: added.join() },
      ...(removed.length) && { filtersRemoved: removed.join() },
      ...(updated.length) && { filtersUpdated: updated.join() },
    }));
  }
}

function getFilterValue(raw: any): string {
  if (raw && !_.isString(raw)) {
    return String(raw);
  }
  return raw;
}

const mapStateToProps: MapStateToProps<FilterUpdaterStateProps> = state => ({
  isLimitedUserVisibilityEnforced: CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(state),
});

const mapDispatchToProps: MapDispatchToProps<FilterUpdaterDispatchProps> = dispatch => bindActionCreators({
  mixedContentFilterApplied,
  setContentFilterNoRequestContent,
}, dispatch);

export const ConnectedFilterUpdater = connect(mapStateToProps, mapDispatchToProps)(FilterUpdater);

/**
 * Creates a higher order component that wraps the given component with the FilterUpdater component.
 */
export function withFilterUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & FilterUpdaterProps) => (
    <ConnectedFilterUpdater {...props}><Component {...props}/></ConnectedFilterUpdater>
  );
}
